import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Header from '@cloudscape-design/components/header';
import Container from '@cloudscape-design/components/container';
import Flashbar, { FlashbarProps } from '@cloudscape-design/components/flashbar';
import { registerSchema } from '../../api/schemas';
import { SchemaBuilder } from '../../components/SchemaBuilder';

interface RegisterSchemaFormProps {
  storeName: string;
  onDone: () => void;
}

export function RegisterSchemaForm({ storeName, onDone }: RegisterSchemaFormProps) {
  const queryClient = useQueryClient();
  const [eventType, setEventType] = useState('');
  const [version, setVersion] = useState('1');
  const [schema, setSchema] = useState<object>({ type: 'object', properties: {} });
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [errors, setErrors] = useState<{ eventType?: string; version?: string; schema?: string }>({});

  const mutation = useMutation({
    mutationFn: () => registerSchema(storeName, eventType, parseInt(version, 10), schema),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemas', storeName] });
      onDone();
    },
    onError: (err: Error) => {
      setFlash([{ type: 'error', content: err.message, dismissible: true, onDismiss: () => setFlash([]) }]);
    },
  });

  const hasFields = schema && Object.keys((schema as any).properties || {}).length > 0;

  const handleSubmit = () => {
    const newErrors: { eventType?: string; version?: string; schema?: string } = {};
    if (!eventType.trim()) newErrors.eventType = 'Event type name is required.';
    if (!version || parseInt(version, 10) < 1) newErrors.version = 'Version must be ≥ 1.';
    if (!hasFields) newErrors.schema = 'Schema must have at least one field.';
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) mutation.mutate();
  };

  return (
    <SpaceBetween size="l">
      <Flashbar items={flash} />
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDone}>Cancel</Button>
            <Button
              variant="primary"
              loading={mutation.isPending}
              onClick={handleSubmit}
            >
              Register schema
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Event type</Header>}>
            <SpaceBetween size="m">
              <FormField
                label="Event type name"
                description="The event type this schema validates. Use PascalCase by convention."
                constraintText="Required"
                errorText={errors.eventType}
              >
                <Input
                  value={eventType}
                  onChange={({ detail }) => setEventType(detail.value)}
                  placeholder="OrderCreated"
                />
              </FormField>
              <FormField
                label="Version"
                description="Schema version. Increment when evolving the schema (additive changes only)."
                constraintText="Required, must be ≥ 1"
                errorText={errors.version}
              >
                <Input
                  value={version}
                  onChange={({ detail }) => setVersion(detail.value)}
                  type="number"
                  inputMode="numeric"
                />
              </FormField>
            </SpaceBetween>
          </Container>

          <Container header={<Header variant="h2">Event schema</Header>}>
            <SchemaBuilder onSchemaGenerated={setSchema} initialSchema={schema} />
          </Container>
        </SpaceBetween>
      </Form>
    </SpaceBetween>
  );
}
